import { ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { SuccessCode, SuccessInfo } from '../../models/success.model';
import { ErrorCode, TechnicalError } from '../../models/errors.model';
import * as MailboxActions from '../../store/actions/mailbox.actions';
import { Alert } from '@proximus/ng-common-components';
import {
  ApplicationState,
  getMailboxesErrorInfo,
  getMailboxesSuccessInfo,
} from '../../../store/reducers';
import { filter } from 'rxjs/operators';
import { AlertsUtilService } from '../../service/alerts.util.service';
import { Observable, of, Subscription } from 'rxjs';

@Component({
  selector: 'mprd-alerts-container',
  templateUrl: 'alerts-container.component.html',
})
export class AlertsContainerComponent implements OnDestroy {
  alerts$: Observable<Alert[]>;
  alerts: Alert[] = [];
  mailboxSuccessSubscription: Subscription;
  mailboxErrorSubscription: Subscription;

  // TODO: refactor this to separate components
  constructor(
    private store: Store<ApplicationState>,
    private alertsUtilService: AlertsUtilService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    this.mailboxSuccessSubscription = store
      .select(getMailboxesSuccessInfo)
      .pipe(filter(Boolean))
      .subscribe((success: SuccessInfo) => {
        this.updateMailboxSuccessInfo(success);
      });

    this.mailboxErrorSubscription = store
      .select(getMailboxesErrorInfo)
      .pipe(filter(Boolean))
      .subscribe((error: TechnicalError) => {
        this.updateMailboxError(error);
      });
    this.alerts$ = of(this.alerts);
  }

  updateMailboxSuccessInfo(successInfo: SuccessInfo) {
    switch (successInfo.code) {
      case SuccessCode.MailboxCreated:
        this.addAlert(
          this.alertsUtilService.getAlertFor(
            'success',
            true,
            'create.mailbox.success.message',
            successInfo.code,
            'create.mailbox.success.title',
          ),
        );
    }

    this.removeAlertByCode(ErrorCode.MailboxCreationFailed);
    this.removeAlertByCode(ErrorCode.MailboxAliasNotUnique);
    this.removeAlertByCode(ErrorCode.PasswordMalformed);
    this.removeAlertByCode(ErrorCode.TechnicalError);
  }

  updateMailboxError(technicalError: TechnicalError) {
    switch (technicalError.code) {
      case ErrorCode.AddressMalformed:
        this.addAlert(
          this.alertsUtilService.getAlertFor(
            'error',
            true,
            'create.mailbox.failure.message',
            ErrorCode.MailboxCreationFailed,
            'create.mailbox.failure.title',
          ),
        );
        break;
      case ErrorCode.MailboxAliasNotUnique:
        this.addAlert(
          this.alertsUtilService.getAlertFor(
            'error',
            true,
            'alias.creation.failure.not.unique.message',
            technicalError.code,
            'alias.creation.failure.not.unique.title',
          ),
        );
        break;
      case ErrorCode.PasswordMalformed:
      case ErrorCode.TechnicalError:
        this.addAlert(
          this.alertsUtilService.getAlertFor(
            'error',
            true,
            'general.technical.error.message',
            technicalError.code,
            'general.technical.error.title',
          ),
        );
        break;
    }
    if (technicalError.code === undefined) {
      this.addAlert(
        this.alertsUtilService.getAlertFor(
          'error',
          true,
          'general.technical.error.message',
          technicalError.code,
          'general.technical.error.title',
        ),
      );
    }

    this.removeAlertByCode(SuccessCode.MailboxCreated);
  }

  private removeAlertByCode(code: SuccessCode | ErrorCode): void {
    if (this.alerts.length > 1) {
      const previousAlerts = this.alerts.filter((alert) => alert.id === code);
      if (previousAlerts.length > 0) {
        this.alerts.splice(this.alerts.indexOf(previousAlerts[0]), 1);
      }
    }
  }

  private addAlert(alert: Alert) {
    if (
      alert &&
      !this.alerts.find((existingAlert) => existingAlert.id === alert.id)
    ) {
      this.alerts.push(alert);
    }
    this.changeDetectorRef.markForCheck();
  }

  ngOnDestroy() {
    this.mailboxErrorSubscription.unsubscribe();
    this.mailboxSuccessSubscription.unsubscribe();
  }

  onCloseAlert(alert: Alert) {
    switch (alert.type) {
      case 'success':
        this.store.dispatch(new MailboxActions.ResetSuccess());
        break;
      case 'error':
        this.store.dispatch(new MailboxActions.ResetError());
        break;
    }
  }
}
